import { useState, useEffect } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText, Inbox, Folder, Plus, Bot, Search, Archive,
  Clock, ChevronRight, X, Send, Loader2, FolderPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/App";


import TopNav from "@/components/TopNav"
import HubSwitcher from "@/components/HubSwitcher";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {};
};

const FOLDER_ICONS = {
  "All Documents": Search,
  "Inbox": Inbox,
  "Workflow": Clock,
  "Drafts": FileText,
  "Archived": Archive,
};

export default function SmartManagementLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [agentResponse, setAgentResponse] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const currentPath = location.pathname;

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/sms/folders`, {
        headers: getHeaders(), withCredentials: true,
      });
      setFolders(res.data.folders || []);
    } catch (err) {
      toast.error("Failed to load folders");
    }
    setLoading(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/sms/folders`,
        { name: newFolderName.trim() },
        { headers: getHeaders(), withCredentials: true },
      );
      setNewFolderName("");
      setShowNewFolder(false);
      fetchFolders();
      toast.success("Folder created");
    } catch (err) {
      toast.error("Failed to create folder");
    }
  };

  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    if (!agentQuery.trim()) return;
    setAgentLoading(true);
    setAgentResponse("");
    try {
      const res = await axios.post(`${API}/sms/agent`,
        { query: agentQuery },
        { headers: getHeaders(), withCredentials: true },
      );
      setAgentResponse(res.data.response);
    } catch (err) {
      setAgentResponse("Sorry, I encountered an error. Please try again.");
    }
    setAgentLoading(false);
  };

  const isActive = (path) => currentPath.startsWith(path);

  const systemFolders = folders.filter(f => f.is_system);
  const customFolders = folders.filter(f => !f.is_system);

  return (
    <>
    <TopNav />
    <div className="flex min-h-screen bg-app">
      {/* SMS Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="p-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800 tracking-wide">SMS</h2>
              <p className="text-[10px] text-slate-500">Smart Management</p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="default"
              className="flex-1 h-7 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500"
              onClick={() => navigate("/hr-hub/smart-management/create")}
            >
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
            <Button size="sm" variant="outline"
              className={`h-7 w-7 p-0 rounded-none ${agentOpen ? 'bg-purple-100 border-purple-300' : ''}`}
              onClick={() => setAgentOpen(!agentOpen)}
            >
              <Bot className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {systemFolders.map((f) => {
            const Icon = FOLDER_ICONS[f.name] || Folder;
            const filterKey = f.name === "All Documents" ? "all"
              : f.name.toLowerCase();
            const folderPath = `/hr-hub/smart-management/dashboard?filter=${filterKey}`;
            return (
              <Link key={f.id} to={folderPath}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors ${
                  currentPath.includes("dashboard") && currentPath.includes(filterKey)
                    ? 'bg-emerald-50 text-emerald-700 font-medium'
                    : 'text-slate-600 hover:bg-app'
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{f.name}</span>
              </Link>
            );
          })}

          {/* Divider */}
          {customFolders.length > 0 && (
            <div className="border-t border-slate-100 my-2" />
          )}

          {customFolders.map((f) => (
            <Link key={f.id}
              to={`/hr-hub/smart-management/dashboard?folder=${f.id}`}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors ${
                currentPath.includes(f.id) ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-slate-600 hover:bg-app'
              }`}
            >
              <Folder className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{f.name}</span>
            </Link>
          ))}

          {/* New folder input */}
          {showNewFolder ? (
            <div className="flex items-center gap-1 px-2 py-1">
              <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                placeholder="Folder name" className="h-7 text-xs rounded-none" autoFocus
                onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
              />
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleCreateFolder}>
                <Plus className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowNewFolder(false)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <button onClick={() => setShowNewFolder(true)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-slate-400 hover:text-slate-600 hover:bg-app w-full"
            >
              <FolderPlus className="w-3.5 h-3.5" />
              <span>New Folder</span>
            </button>
          )}
        </nav>

        {/* Bottom links */}
        <div className="p-2 border-t border-slate-100">
          <Link to="/hr-hub/smart-management/admin"
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors ${
              isActive("/hr-hub/smart-management/admin")
                ? 'bg-emerald-50 text-emerald-700 font-medium'
                : 'text-slate-500 hover:bg-app'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Admin</span>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-app flex">
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
        <div className="hidden lg:block w-40 shrink-0 p-3 border-l border-app">
          <HubSwitcher />
        </div>
      </main>

      {/* Agent SMS Panel */}
      {agentOpen && (
        <aside className="w-80 bg-white border-l border-slate-200 flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-purple-600" />
              <span className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800">Agent SMS</span>
            </div>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setAgentOpen(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            {agentResponse && (
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs text-purple-800 whitespace-pre-wrap">
                {agentResponse}
              </div>
            )}
            {!agentResponse && !agentLoading && (
              <p className="text-xs text-slate-400 text-center mt-8">
                Ask Agent SMS about documents,<br />workflows, or recommendations.
              </p>
            )}
            {agentLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
              </div>
            )}
          </div>
          <form onSubmit={handleAgentSubmit} className="p-3 border-t border-slate-100 flex gap-2">
            <Input value={agentQuery} onChange={e => setAgentQuery(e.target.value)}
              placeholder="Ask Agent SMS..." className="h-8 text-xs rounded-none flex-1"
            />
            <Button type="submit" size="sm" className="h-8 w-8 p-0 rounded-none bg-purple-600 hover:bg-purple-500">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
        </aside>
      )}
    </div>
    </>
  );
}
